import React, { useState, useMemo, useRef } from "react";
import styles from "../styles/Events.module.css";
import {
  FaRegCalendarAlt,
  FaMapMarkerAlt,
  FaRegHeart,
  FaHeart,
  FaSearch,
  FaChevronDown,
} from "react-icons/fa";
import heroImg from "../assets/images/image7.png";
import event1 from "../assets/images/c8.jpeg";
import event2 from "..//assets/images/c3.jpeg";
import event3 from "../assets/images/c5.jpeg";
import ctaBg from "../assets/images/image7.png";

function useInView(ref, { threshold = 0.1, once = true } = {}) {
  const [visible, setVisible] = React.useState(false);
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          if (once) obs.disconnect();
        }
      },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return visible;
}

const initialEvents = [
  { id: 1, name: "21 Days Prayer & Fasting 2025",   date: "August 4–24, 2025",      location: "Zindiro",   desc: "Three weeks of fervent intercession and spiritual depth — join thousands seeking God together.",                    img: event1, type: "upcoming", liked: false, category: "Prayer" },
  { id: 2, name: "Africa Ignite Connection 2024",    date: "August 5–7, 2024",       location: "Zindiro",   desc: "A cross-continental gathering uniting believers across Africa in worship, prayer, and the Word.",                 img: event2, type: "upcoming", liked: false, category: "Connection" },
  { id: 3, name: "Family Week Connection",           date: "September 10–12, 2024",  location: "Ottawa",    desc: "Celebrate family in Christ — a weekend full of teaching, activities, and meaningful fellowship.",              img: event3, type: "past",     liked: false, category: "Connection" },
  { id: 4, name: "21 Days Prayer & Fasting 2025",   date: "August 4–24, 2025",      location: "Europe",    desc: "Three weeks of fervent intercession and spiritual depth — join thousands seeking God together.",                img: event1, type: "upcoming", liked: false, category: "Prayer" },
  { id: 5, name: "Africa Ignite Connection 2024",   date: "August 5–7, 2024",       location: "Mbyo",      desc: "A cross-continental gathering uniting believers across Africa in worship, prayer, and the Word.",               img: event2, type: "upcoming", liked: false, category: "Connection" },
  { id: 6, name: "Family Week Connection",          date: "September 10–12, 2024",  location: "Karangazi", desc: "Celebrate family in Christ — a weekend full of teaching, activities, and meaningful fellowship.",              img: event3, type: "past",     liked: false, category: "Connection" },
];

const tabs       = [{ key: "all", label: "All Events" }, { key: "upcoming", label: "Upcoming" }, { key: "past", label: "Past Events" }];
const categories = ["All Categories", "Prayer", "Connection"];

export default function Events() {
  const [activeTab, setActiveTab]             = useState("all");
  const [events, setEvents]                   = useState(initialEvents);
  const [selectedCategory, setSelectedCategory] = useState("All Categories");
  const [searchQuery, setSearchQuery]         = useState("");

  const gridRef    = useRef(null);
  const ctaRef     = useRef(null);
  const gridVisible = useInView(gridRef, { threshold: 0.05 });
  const ctaVisible  = useInView(ctaRef,  { threshold: 0.15 });

  const filteredEvents = useMemo(() => {
    let f = events;
    if (activeTab !== "all")                        f = f.filter(e => e.type === activeTab);
    if (selectedCategory !== "All Categories")      f = f.filter(e => e.category === selectedCategory);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      f = f.filter(e =>
        e.name.toLowerCase().includes(q) ||
        e.location.toLowerCase().includes(q) ||
        e.desc.toLowerCase().includes(q) ||
        (e.category && e.category.toLowerCase().includes(q))
      );
    }
    return f;
  }, [events, activeTab, selectedCategory, searchQuery]);

  const handleLike = (id) =>
    setEvents(prev => prev.map(e => e.id === id ? { ...e, liked: !e.liked } : e));

  const clearFilters = () => { setSelectedCategory("All Categories"); setSearchQuery(""); };
  const hasActiveFilters = selectedCategory !== "All Categories" || searchQuery.trim() !== "";

  return (
    <div className={styles.eventsPage}>

      {/* ── Cinematic Hero ── */}
      <section className={styles.hero}>
        <img src={heroImg} alt="CWFM Events" className={styles.heroImg} />
        <div className={styles.heroOverlay} />
        <div className={styles.heroContent}>
          <span className={styles.eyebrow}>CWFM · Calendar</span>
          <h1 className={styles.heroTitle}>Events at Calvary</h1>
          <div className={styles.titleBar} />
          <p className={styles.heroSub}>
            Join us for worship, teaching, and fellowship. Discover what is
            happening at Calvary Wide Fellowship Ministries.
          </p>
        </div>
        <div className={styles.scrollCue}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2.5">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </div>
      </section>

      {/* ── Cards Section (light) ── */}
      <section className={styles.cardsOuter}>
        <div className={styles.cardsSection}>

          {/* Tabs */}
          <div className={styles.tabs}>
            {tabs.map(tab => (
              <button
                key={tab.key}
                className={`${styles.tabBtn} ${activeTab === tab.key ? styles.active : ""}`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Search + Filter */}
          <div className={styles.filterBar}>
            <div className={styles.searchWrapper}>
              <FaSearch className={styles.searchIcon} />
              <input
                type="text"
                placeholder="Search events by name, location, category..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className={styles.searchInput}
              />
              {searchQuery && (
                <button className={styles.clearSearch} onClick={() => setSearchQuery("")} aria-label="Clear search">×</button>
              )}
            </div>
            <div className={styles.filterDropdown}>
              <select
                value={selectedCategory}
                onChange={e => setSelectedCategory(e.target.value)}
                className={styles.filterSelect}
              >
                {categories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <FaChevronDown className={styles.dropdownIcon} />
            </div>
          </div>

          {/* Active Filters */}
          {hasActiveFilters && (
            <div className={styles.activeFilters}>
              <span className={styles.filterCount}>
                {filteredEvents.length} event{filteredEvents.length !== 1 ? "s" : ""} found
                {selectedCategory !== "All Categories" && ` in "${selectedCategory}"`}
                {searchQuery.trim() && ` matching "${searchQuery}"`}
              </span>
              <button className={styles.clearFiltersBtn} onClick={clearFilters}>Clear all filters</button>
            </div>
          )}

          {/* Events Grid */}
          <div
            ref={gridRef}
            className={`${styles.eventsGrid} ${gridVisible ? styles.gridVisible : ""}`}
          >
            {filteredEvents.length === 0 ? (
              <div className={styles.noEvents}>
                <div className={styles.noEventsIcon}>📅</div>
                <h3>No events found</h3>
                <p>Try adjusting your filters or search terms</p>
                {hasActiveFilters && (
                  <button className={styles.resetBtn} onClick={clearFilters}>Reset Filters</button>
                )}
              </div>
            ) : (
              filteredEvents.map((event, i) => (
                <div
                  className={styles.eventCard}
                  key={event.id}
                  style={{ transitionDelay: `${i * 70}ms` }}
                >
                  <div className={styles.eventImgWrap}>
                    <img src={event.img} alt={event.name} className={styles.eventImg} />
                    <div className={styles.imgOverlay} />
                    {event.category && (
                      <span className={styles.categoryBadge}>{event.category}</span>
                    )}
                  </div>
                  <div className={styles.eventInfo}>
                    <h3 className={styles.eventName}>{event.name}</h3>
                    <div className={styles.eventMetaWrapper}>
                      <div className={styles.eventMeta}><FaRegCalendarAlt className={styles.icon} />{event.date}</div>
                      <div className={styles.eventMeta}><FaMapMarkerAlt className={styles.icon} />{event.location}</div>
                    </div>
                    <p className={styles.eventDesc}>{event.desc}</p>
                    <button
                      className={styles.likeBtn}
                      onClick={() => handleLike(event.id)}
                      aria-label={event.liked ? "Unlike event" : "Like event"}
                    >
                      {event.liked
                        ? <FaHeart className={styles.likedIcon} />
                        : <FaRegHeart className={styles.likeIcon} />}
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section
        ref={ctaRef}
        className={`${styles.ctaSection} ${ctaVisible ? styles.ctaVisible : ""}`}
        style={{ backgroundImage: `url(${ctaBg})` }}
      >
        <div className={styles.ctaOverlay} />
        <div className={styles.ctaContent}>
          <span className={styles.ctaEyebrow}>Get Involved</span>
          <h2 className={styles.ctaTitle}>Host or Sponsor an Event</h2>
          <div className={styles.ctaTitleBar} />
          <p className={styles.ctaDesc}>
            Contact our events team to get involved or to learn more about
            partnership opportunities at CWFM.
          </p>
          <a href="/contact" className={styles.ctaButton}>Contact Us</a>
        </div>
      </section>

    </div>
  );
}
